import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { authAPI } from '../config/api';

const Register = () => {
  const [formData, setFormData] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const passRegex = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*]).{6,}$/;
    if (!passRegex.test(formData.password)) {
      setError('Password must be at least 6 characters with a letter, number, and special character.');
      return;
    }

    setLoading(true);
    try {
      const res = await authAPI.register(formData);
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate(returnUrl || '/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-4 py-3 text-[#1A1A2E] text-sm focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]";

  return (
    <div data-testid="register-page" className="pt-28 min-h-screen flex items-center justify-center bg-[#F7F8FA]">
      <div className="w-full max-w-md mx-6">
        <div className="bg-white rounded-xl p-8 md:p-10 shadow-lg border border-gray-100">
          <h1 className="text-3xl text-center mb-2 text-[#1A1A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Create <span className="text-[#F57C20] italic">Account</span>
          </h1>
          <p className="text-[#6B7280] text-sm text-center mb-8">Register to book your villa stay</p>

          {error && <div data-testid="register-error" className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 mb-6 rounded-lg">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Full Name</label>
              <input type="text" value={formData.full_name} onChange={e => setFormData({...formData, full_name: e.target.value})} required data-testid="register-name" className={inputCls} />
            </div>
            <div>
              <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Email</label>
              <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required data-testid="register-email" className={inputCls} />
            </div>
            <div>
              <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Phone</label>
              <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required data-testid="register-phone" className={inputCls} />
            </div>
            <div>
              <label className="text-[#1A1A2E] text-xs font-semibold tracking-wider uppercase block mb-2">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required data-testid="register-password" className={inputCls + " pr-12"} placeholder="Min 6 chars: letter + number + special" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="register-toggle-password" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#F57C20] transition-colors">
                  {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full bg-[#F57C20] text-white py-4 text-sm font-semibold tracking-wide rounded-lg hover:bg-[#E06A10] transition-all disabled:opacity-50">
              {loading ? 'Creating Account...' : 'Register'}
            </button>
          </form>

          <p className="text-[#6B7280] text-sm text-center mt-6">
            Already have an account?{' '}
            <Link to={`/login${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`} className="text-[#F57C20] font-semibold hover:underline">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
